import type { SceneAudioStimulus } from "../config/scene-stimulus-schema";
import { shuffled } from "../core/collections";
import { deriveSeed, DeterministicRandom } from "../core/random";

/**
 * Expands three scene clips into five decisions: every clip occurs once,
 * two seeded clips repeat once, and identical clips never occur consecutively.
 */
export function buildFiveDecisionAudioSchedule(
  clips: readonly SceneAudioStimulus[],
  sessionSeed: number,
  sceneId: string,
): SceneAudioStimulus[] {
  if (clips.length !== 3 || new Set(clips.map((clip) => clip.id)).size !== 3) {
    throw new Error("Five-decision audio scheduling requires three unique clips");
  }
  const random = new DeterministicRandom(deriveSeed(sessionSeed, `scene-audio:${sceneId}`));
  const repeatClips = shuffled(clips, random).slice(0, 2);
  const pool = [...clips, ...repeatClips];
  for (let attempt = 0; attempt < 64; attempt += 1) {
    const candidate = shuffled(pool, random);
    if (candidate.every((clip, index) => index === 0 || clip.id !== candidate[index - 1]?.id)) {
      return candidate;
    }
  }
  // With counts 2+2+1 a valid ordering always exists; this is a deterministic
  // construction retained as a defensive fallback if the shuffle loop changes.
  const singles = shuffled(clips, random);
  const first = repeatClips[0];
  const second = repeatClips[1];
  const once = singles.find((clip) => clip.id !== first?.id && clip.id !== second?.id);
  if (!first || !second || !once) throw new Error("Failed to construct the five-clip schedule");
  return [first, second, once, first, second];
}
